import { EChartsOption } from "echarts";
import moment from "moment";
import { unionBy, lowerCase } from "lodash";

import { useDateTimeFormatStore } from "hooks/useDateTimeFormatStore";
import { SensorChartType, ChartSource } from "constant/chart";
import { CHART_DUPLICATE_COLOR_MESSAGE } from "constant";

export function getCommonXAxis(option: EChartsOption): any {
    const { is12HourFormat } = useDateTimeFormatStore.getState();
    const xAxisTimeFormat = is12HourFormat
        ? "{hh}:{mm}:{ss} {A}"
        : "{HH}:{mm}:{ss}";
    const xAxisTimeFormatNoSeconds = is12HourFormat
        ? "{hh}:{mm} {A}"
        : "{HH}:{mm}";

    return {
        type: "time",
        axisLabel: {
            formatter: {
                // Use custom time format for granularity less than 1 day
                hour: xAxisTimeFormatNoSeconds,
                minute: xAxisTimeFormatNoSeconds,
                second: xAxisTimeFormat,
                millisecond: xAxisTimeFormat,
            },
            hideOverlap: true,
        },
        // Only spread xAxis if option exists
        ...(option?.xAxis || {}),
    };
}

export function getCommonDataZoom(
    option: EChartsOption,
    chartZoom: { start: number; end: number }
): any[] {
    // Handle null/undefined option
    if (!option) {
        return [];
    }

    const raw = option.dataZoom
        ? Array.isArray(option.dataZoom)
            ? option.dataZoom
            : [option.dataZoom]
        : [];

    return raw.map(() => ({
        start: chartZoom.start,
        end: chartZoom.end,
        labelFormatter: (val: number) => {
            const { longDateTimeFormat } = useDateTimeFormatStore.getState();
            return moment(new Date(val), "x").format(longDateTimeFormat);
        },
    }));
}

/**
 * Generates invisible characters to make duplicate names unique in chart legends.
 * Uses zero-width characters that are not affected by IDE formatting.
 * @param index The index to generate unique invisible suffix for
 * @returns A string of invisible characters
 */
export const getInvisibleSuffix = (index: number): string => {
    // Using zero-width space (U+200B) repeated based on index
    // This is invisible but makes each name unique for the legend
    return "\u200B".repeat(index);
};

export const validateChartColors = (params: {
    chart: SensorChartType;
    source: ChartSource;
    attributes: any[];
}) => {
    const validates = [];

    // Only validate color if the chart has color attribute. For example, heatmap chart does not have color attribute.
    const hasColor = params.attributes.some((attr: any) => !!attr.color);

    // The combined chart just has 1 color except the Aggregation ones (Consumption).
    // For ex: PCS combined and Consumption are combined charts but the PCS has only 1 color, Consumption has multiple ones (color not combined).
    const isColorCombined =
        hasColor &&
        params.chart === SensorChartType.COMBINED &&
        params.source !== ChartSource.AGGREGATION;

    if (hasColor && !isColorCombined) {
        const uniqueColors = unionBy(params.attributes, ({ color }) =>
            lowerCase(color)
        );

        if (uniqueColors.length < params.attributes.length) {
            validates.push({
                message: CHART_DUPLICATE_COLOR_MESSAGE,
            });
        }
    }

    return validates;
};
